// ═══════════════════════════════════════════════════════════════════════════
// InvoicePreview
// Extraído VERBATIM de index.html v0.9.16.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getIepsRateForGL } from "../dominio/impuestos.js";
import { SAT_FORMA_PAGO, SAT_METODO_PAGO, SAT_USO_CFDI } from "../fiscal/catalogos-sat.js";
import { REMITENTE, fmt } from "../pdf/comunes.js";
import { LOGO_BASE64 } from "../pdf/logo.js";
import { generatePDF } from "../pdf/remision.js";
import { btnPrimary, btnSecondary } from "./estilos.js";

export function InvoicePreview({ folio, client, lines, products, getPrice, discount, ivaRate, subtotalBeforeDiscount, discountAmount, subtotal, iepsAmount, iepsBreakdown, ivaAmount, total, totalBottles, notes, formaPago, metodoPago, usoCfdi, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflow: "auto", padding: 20 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", color: "#111", maxWidth: 800, width: "100%", borderRadius: 8, padding: "40px 48px", marginTop: 20, fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <img src={LOGO_BASE64} alt="Vinícola Total" style={{ width: 64, height: 64, objectFit: "contain" }} />
            <div>
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, margin: 0, color: "#333" }}>ORDEN DE REMISIÓN</h1>
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{new Date().toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" })}</div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "#888" }}>FOLIO:</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#c9a96e" }}>{folio}</div>
          </div>
        </div>

        {/* Addresses */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, marginBottom: 24, paddingBottom: 20, borderBottom: "2px solid #eee" }}>
          <div>
            <div style={{ fontSize: 10, color: "#888", letterSpacing: 2, marginBottom: 6 }}>REMITENTE</div>
            <div style={{ fontWeight: 600 }}>{REMITENTE.name}</div>
            <div>RFC: {REMITENTE.rfc}</div>
            <div>{REMITENTE.street}</div>
            <div>{REMITENTE.colonia}, CP {REMITENTE.cp}</div>
            <div>{REMITENTE.city}, {REMITENTE.state}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#888", letterSpacing: 2, marginBottom: 6 }}>DESTINATARIO</div>
            <div style={{ fontWeight: 600 }}>{client.name}</div>
            {client.rfc && <div>RFC: {client.rfc}</div>}
            <div>{client.street}</div>
            {client.colonia && <div>{client.colonia}, CP {client.cp}</div>}
            <div>{client.city}, {client.state}</div>
            {client.phone && <div>Tel: {client.phone}</div>}
            {client.att && <div>Att: {client.att}</div>}
          </div>
        </div>

        {/* CFDI info */}
        <div style={{ fontSize: 11, color: "#555", marginBottom: 16 }}>
          <div>USO DE CFDI: {SAT_USO_CFDI.find(u => u.code === usoCfdi)?.label || usoCfdi}</div>
          <div>FORMA DE PAGO: {SAT_FORMA_PAGO.find(f => f.code === formaPago)?.label || formaPago} · Método: {SAT_METODO_PAGO.find(m => m.code === metodoPago)?.label || metodoPago}</div>
        </div>

        {/* Line Items */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #ddd" }}>
              <th style={{ textAlign: "left", padding: "8px 4px", fontSize: 10, color: "#888" }}>CANT.</th>
              <th style={{ textAlign: "left", padding: "8px 4px", fontSize: 10, color: "#888" }}>UNIDAD</th>
              <th style={{ textAlign: "left", padding: "8px 4px", fontSize: 10, color: "#888" }}>DESCRIPCIÓN</th>
              <th style={{ textAlign: "right", padding: "8px 4px", fontSize: 10, color: "#888" }}>P. BRUTO</th>
              <th style={{ textAlign: "right", padding: "8px 4px", fontSize: 10, color: "#888" }}>IMPORTE</th>
            </tr>
          </thead>
          <tbody>
            {lines.map(li => {
              const prod = products.find(p => p.id === li.productId);
              const price = getPrice(li.productId);
              const isCortesia = price === 0;
              return (
                <tr key={li.productId} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "8px 4px" }}>{li.qty}</td>
                  <td style={{ padding: "8px 4px" }}>botella</td>
                  <td style={{ padding: "8px 4px" }}>{prod?.name}</td>
                  <td style={{ textAlign: "right", padding: "8px 4px", color: isCortesia ? "#5ba8a8" : undefined }}>{isCortesia ? "—" : fmt(price)}</td>
                  <td style={{ textAlign: "right", padding: "8px 4px", color: isCortesia ? "#5ba8a8" : undefined }}>{isCortesia ? "cortesía" : fmt(price * li.qty)}</td>
                </tr>
              );
            })}
            <tr style={{ borderTop: "2px solid #ddd", fontWeight: 600 }}>
              <td style={{ padding: "8px 4px" }}>{totalBottles}</td>
              <td colSpan={2} style={{ padding: "8px 4px" }}>BOTELLAS</td>
              <td />
              <td style={{ textAlign: "right", padding: "8px 4px" }}>{fmt(subtotalBeforeDiscount)}</td>
            </tr>
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ maxWidth: 280, marginLeft: "auto" }}>
          {discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
            <span style={{ color: "#888" }}>Descuento ({discount}%)</span><span>-{fmt(discountAmount)}</span>
          </div>}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
            <span style={{ color: "#888" }}>Base gravable</span><span>{fmt(subtotal)}</span>
          </div>
          {(iepsBreakdown || []).map((b, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
              <span style={{ color: "#888" }}>IEPS ({b.rate}%)</span><span>{fmt(b.ieps)}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
            <span style={{ color: "#888" }}>IVA ({ivaRate}%)</span><span>{fmt(ivaAmount)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 0", borderTop: "2px solid #333", marginTop: 8, fontSize: 16, fontWeight: 700 }}>
            <span>TOTAL NETO</span><span>{fmt(total)}</span>
          </div>
          <div style={{ textAlign: "right", fontSize: 11, color: "#888", marginTop: 4 }}>
            Total botellas: {totalBottles}
          </div>
        </div>

        {/* Bank details */}
        <div style={{ marginTop: 32, padding: 16, background: "#f8f6f2", borderRadius: 8, fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Datos bancarios</div>
          <div>{REMITENTE.name}</div>
          <div>{REMITENTE.bank}</div>
          <div>CLABE: {REMITENTE.clabe}</div>
          <div style={{ marginTop: 6 }}>Comprobante de pago: Whatsapp {REMITENTE.whatsapp} · {REMITENTE.email}</div>
        </div>

        {notes && (
          <div style={{ marginTop: 16, padding: 12, background: "#fff9e6", borderRadius: 8, border: "1px solid #f0e0a0", fontSize: 12 }}>
            <strong>Notas:</strong> {notes}
          </div>
        )}

        <div style={{ marginTop: 24, display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ ...btnSecondary, color: "#333", borderColor: "#ccc" }}>Cerrar</button>
          <button onClick={() => {
            const invData = { folio, client, lines: lines.map(li => {
              const prod = products.find(p => p.id === li.productId);
              const price = getPrice(li.productId);
              return { ...li, productName: prod?.name, unitPrice: price, amount: price * li.qty,
                iepsRate: prod?.iepsRate ?? getIepsRateForGL(prod?.gl ?? 13) };
            }), discount, ivaRate, subtotalBeforeDiscount, discountAmount, subtotal, iepsAmount, iepsBreakdown, ivaAmount, total, totalBottles, notes,
            formaPago, metodoPago, usoCfdi, date: new Date().toISOString() };
            generatePDF(invData);
          }} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }}>
            📄 Descargar PDF
          </button>
        </div>
      </div>
    </div>
  );
}
